/**
 * PreviewViewContent.tsx - PreviewViewContent - Content display for PreviewView
 * 
 * CHANGE HISTORY:
 * - 251021_0641 [a6c7876] danielbeer: docs(inspectr): complete upgrade documentation
 */
import React, { useMemo } from 'react';

export interface PreviewViewContentProps {
  content: string;
  format: string;
  searchQuery: string;
  syntaxHighlight: boolean;
  maxHeight: string;
  theme: 'light' | 'dark';
}

const PreviewViewContent: React.FC<PreviewViewContentProps> = ({
  content,
  format,
  searchQuery,
  syntaxHighlight,
  maxHeight,
  theme
}) => {
  // Apply syntax highlighting for JSON
  const highlightedContent = useMemo(() => {
    if (!syntaxHighlight || format !== 'json') {
      return content;
    }

    return content.replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
      (match) => {
        let cls = 'number';
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = 'key';
          } else {
            cls = 'string';
          }
        } else if (/true|false/.test(match)) {
          cls = 'boolean';
        } else if (/null/.test(match)) {
          cls = 'null';
        }
        return `<span class="syntax-${cls}">${match}</span>`;
      }
    );
  }, [content, format, syntaxHighlight]);

  // Apply search highlighting
  const searchHighlightedContent = useMemo(() => {
    if (!searchQuery) {
      return highlightedContent;
    }

    const escapedQuery = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    
    return highlightedContent.replace(regex, '<mark class="search-highlight">$1</mark>');
  }, [highlightedContent, searchQuery]);

  // Determine if we should use HTML rendering
  const useHtml = syntaxHighlight && format === 'json' || searchQuery;

  return (
    <div 
      className="preview-view-content"
      style={{ maxHeight }}
    >
      <pre className={`preview-view-pre preview-view-format-${format}`}>
        {useHtml ? (
          <code 
            className="preview-view-code"
            dangerouslySetInnerHTML={{ __html: searchHighlightedContent }}
          />
        ) : (
          <code className="preview-view-code">
            {content}
          </code>
        )}
      </pre>
    </div>
  );
};

export default PreviewViewContent;
